import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookHeart, Plus, Sparkles, Download, Clock, Palette, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import client, { mediaUrl } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { TerryCorner } from "@/components/Terry";
import OrdersPanel from "@/components/OrdersPanel";
import BookActions from "@/components/BookActions";
import MemoryJar from "@/components/MemoryJar";

// Manual-mode book states. A book with no pages is one our illustrators are
// still working on, so it must not link into the reader.
const STATUS_META = {
    draft:       { label: "Not paid yet",  icon: Clock,   tone: "text-plum/50" },
    queued:      { label: "In the queue",  icon: Clock,   tone: "text-plum/60" },
    in_progress: { label: "Being drawn",   icon: Palette, tone: "text-bubblegum" },
    revision:    { label: "Making changes", icon: Palette, tone: "text-bubblegum" },
    delivered:   { label: "Ready",         icon: BookHeart, tone: "text-bubblegum" },
};

// Books delivered before versioning still report a single v1 from the server.
const versionsOf = (b) => b.pdf_versions || [];

export default function Library() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [chars, setChars] = useState([]);
    const [books, setBooks] = useState([]);
    const [downloading, setDownloading] = useState(null);

    // The PDF route is auth-gated, so it cannot be a plain href — fetch it with
    // the bearer token and hand the browser a blob.
    const downloadPdf = async (b, version) => {
        setDownloading(`${b.id}:${version ?? "latest"}`);
        try {
            const res = await client.get(`/books/${b.id}/pdf`, {
                responseType: "blob",
                params: version ? { version } : undefined,
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(b.title || "storybook").replace(/[^a-z0-9 \-_]/gi, "")}${version ? ` v${version}` : ""}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.status === 409
                ? "Your book is still being illustrated."
                : "Could not download your book.");
        } finally { setDownloading(null); }
    };

    const loadBooks = useCallback(
        () => client.get("/books").then(({ data }) => setBooks(data.books)).catch(() => {}), []);

    useEffect(() => {
        if (!user) { nav("/auth"); return; }
        Promise.all([
            client.get("/characters").then(({ data }) => setChars(data.characters)),
            loadBooks(),
        ]).catch(() => {});
    }, [user, nav, loadBooks]);

    return (
        <div className="min-h-screen magic-bg">
            <Toaster position="top-center" richColors />
            <Navbar />
            <TerryCorner pose="walk" />
            <div className="max-w-6xl mx-auto px-6 py-12">
                <div className="flex flex-wrap gap-4 items-end justify-between mb-10">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-tight">
                            {user?.name ? `${user.name}'s bookshelf` : "Your bookshelf"}
                        </h1>
                        <div className="script-note text-3xl mt-1">the people, places and stories you keep</div>
                    </div>
                    <Link to="/create">
                        <button className="btn-sticker text-sm" data-testid="library-create-btn">
                            <Plus className="w-4 h-4 mr-1 inline" /> New story
                        </button>
                    </Link>
                </div>

                <section className="mb-14">
                    <div className="font-display text-2xl mb-4">Your cast</div>
                    {chars.length === 0 ? (
                        <div className="card-cloud p-8 text-center text-plum/60">
                            No characters yet. Start your first story to save loved ones here.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="library-cast">
                            {chars.map((c, i) => (
                                <motion.div key={c.id} animate={{ y: [0, -3, 0] }} transition={{ duration: 4 + (i % 3), repeat: Infinity }}
                                    className="card-cloud p-4 text-center">
                                    <img src={c.photo_b64} className="w-full aspect-square object-cover rounded-lg mb-3" alt="" />
                                    <div className="font-medium text-sm">{c.name}</div>
                                    <div className="text-xs text-plum/50 capitalize">{c.role_label || c.role}</div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </section>

                <section className="mb-14">
                    <div className="font-display text-2xl mb-1">The memory jar</div>
                    <p className="text-plum/55 text-sm mb-4 max-w-2xl">
                        One question a week. One line back. Every answer becomes a page —
                        and none of it can be written by anyone who wasn't there.
                    </p>
                    <MemoryJar />
                </section>

                <OrdersPanel />

                <section>
                    <div className="font-display text-2xl mb-4">Your storybooks</div>
                    {books.length === 0 ? (
                        <div className="card-cloud p-8 text-center text-plum/60 flex flex-col items-center gap-4">
                            <BookHeart className="w-8 h-8 text-bubblegum" />
                            <div>No stories yet. Ready for your first one?</div>
                            <Link to="/create"><Button className="rounded-full bg-bubblegum hover:bg-bubblegum/90">
                                <Sparkles className="w-4 h-4 mr-1" /> Create Your Story
                            </Button></Link>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 items-start" data-testid="library-books">
                            {books.map((b) => {
                                const meta = STATUS_META[b.status] || { label: b.status, icon: Clock, tone: "text-plum/50" };
                                const readable = b.pages?.length > 0;
                                const hasCover = !!b.pages?.[0]?.image_url;
                                const Icon = meta.icon;
                                const card = (
                                    <>
                                        {hasCover && (
                                            <div className="aspect-[3/4] overflow-hidden bg-lavender/50">
                                                <img src={mediaUrl(b.pages[0].image_url)} alt=""
                                                    className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                                            </div>
                                        )}
                                        <div className="p-4">
                                            {/* Two-up on a phone leaves ~145px per card, so the tile
                                                sitting beside the title clipped it — it stacks until
                                                there is room for a row. */}
                                            <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-3">
                                                {/* Without a cover the tile carries the status colour, so the
                                                    card still reads at a glance without a photo-shaped hole. */}
                                                {!hasCover && (
                                                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl shrink-0 grid place-items-center
                                                                    bg-gradient-to-br from-lavender/80 to-rose/50">
                                                        <Icon className={`w-5 h-5 ${meta.tone}`} />
                                                    </div>
                                                )}
                                                <div className="min-w-0 w-full flex-1">
                                                    <div className="font-display text-base sm:text-lg leading-tight line-clamp-2 break-words">{b.title || "Story in progress"}</div>
                                                    <div className={`text-xs mt-0.5 ${meta.tone}`}>{meta.label} · {b.page_count} pages</div>
                                                </div>
                                            </div>
                                            {b.status === "delivered" && versionsOf(b).length > 0 && (() => {
                                                const vs = versionsOf(b);
                                                const latest = vs[vs.length - 1];
                                                const earlier = vs.slice(0, -1).reverse();
                                                const key = `${b.id}:latest`;
                                                return (
                                                    <div className="mt-3">
                                                        <button onClick={(e) => { e.preventDefault(); downloadPdf(b); }}
                                                            disabled={downloading === key}
                                                            data-testid={`download-pdf-${b.id}`}
                                                            className="w-full rounded-full min-h-9 py-2 px-2 text-[11px] leading-tight font-medium bg-bubblegum text-white hover:bg-bubblegum/90 flex items-center justify-center gap-1 disabled:opacity-60">
                                                            {downloading === key
                                                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing…</>
                                                                : <><Download className="w-3.5 h-3.5 shrink-0" /> Download PDF{vs.length > 1 ? ` v${latest.version}` : ""}</>}
                                                        </button>
                                                        {/* Every earlier delivery stays here — asking for a change
                                                            must never take away the book you already had. */}
                                                        {earlier.length > 0 && (
                                                            <div className="mt-2" data-testid={`versions-${b.id}`}>
                                                                <div className="text-[10px] uppercase tracking-[0.16em] text-plum/45 mb-1">
                                                                    Earlier versions
                                                                </div>
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {earlier.map((v) => (
                                                                        <button key={v.version}
                                                                            onClick={(e) => { e.preventDefault(); downloadPdf(b, v.version); }}
                                                                            disabled={downloading === `${b.id}:${v.version}`}
                                                                            data-testid={`download-pdf-${b.id}-v${v.version}`}
                                                                            title={v.delivered_at ? `Delivered ${new Date(v.delivered_at).toLocaleDateString()}` : ""}
                                                                            className="rounded-full px-2.5 py-1 text-[10px] font-medium border border-plum/15 text-plum/70 hover:bg-plum/5 disabled:opacity-60 flex items-center gap-1">
                                                                            {downloading === `${b.id}:${v.version}`
                                                                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                                                                : <Download className="w-3 h-3" />}
                                                                            v{v.version}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            {b.status === "draft" && (
                                                <Link to={`/create?book=${b.id}`}
                                                    data-testid={`finish-checkout-${b.id}`}
                                                    className="mt-3 w-full rounded-full min-h-9 py-2 px-2 text-[11px] leading-tight font-medium text-center
                                                               bg-bubblegum text-white hover:bg-bubblegum/90 flex items-center justify-center gap-1">
                                                    <Sparkles className="w-3.5 h-3.5 shrink-0" /> Finish checkout
                                                </Link>
                                            )}
                                            <BookActions book={b} user={user} onChanged={loadBooks} />
                                        </div>
                                    </>
                                );
                                return readable ? (
                                    <Link key={b.id} to={`/read/${b.id}`} className="card-cloud overflow-hidden group">{card}</Link>
                                ) : (
                                    <div key={b.id} className="card-cloud overflow-hidden group">{card}</div>
                                );
                            })}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
